import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useState, useEffect } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { Id } from "../../../../../convex/_generated/dataModel";

interface EditLeadEventSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onEventUpdated?: () => void;
  event: any; // The event to edit
}

export function EditLeadEventSheet({
  isOpen,
  onClose,
  onEventUpdated,
  event,
}: EditLeadEventSheetProps) {
  // State for form fields
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("10:00");
  const [isAllDay, setIsAllDay] = useState(false);
  const [eventType, setEventType] = useState<string>("walkthrough");
  const [location, setLocation] = useState("");
  const [address, setAddress] = useState("");
  const [assignedStaff, setAssignedStaff] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch organization members
  const members = useQuery(api.calendar.getOrgMembers) || [];

  // Update event mutation
  const updateEvent = useMutation(api.calendar.updateEvent);

  // Populate form when event changes or sheet opens
  useEffect(() => {
    if (isOpen && event) {
      setTitle(event.title || "");
      setDescription(event.description || "");
      setStartDate(event.startDate || "");
      setEndDate(event.endDate || "");
      setStartTime(event.startTime || "09:00");
      setEndTime(event.endTime || "10:00");
      setIsAllDay(event.isAllDay || false);
      setEventType(event.eventType || "walkthrough");
      setLocation(event.location || "");
      setAddress(event.address || "");
      setAssignedStaff(event.assignedStaff || []);
      setIsSubmitting(false);
    }
  }, [isOpen, event]);

  // Handle form submission
  const handleSubmit = async () => {
    if (!title || !startDate || !startTime || !endTime || assignedStaff.length === 0) {
      return;
    }

    setIsSubmitting(true);
    try {
      await updateEvent({
        id: event._id,
        title,
        description,
        startDate,
        endDate: endDate || startDate,
        startTime,
        endTime,
        isAllDay,
        eventType: eventType as any,
        location,
        address,
        assignedStaff: assignedStaff as Id<"users">[],
      });

      onEventUpdated?.();
      onClose();
    } catch (error) {
      console.error("Error updating event:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle staff assignment toggle
  const toggleStaffAssignment = (memberId: string) => {
    setAssignedStaff((prev) =>
      prev.includes(memberId) ? prev.filter((id) => id !== memberId) : [...prev, memberId]
    );
  };

  // Limited event types for leads
  const leadEventTypes = [
    { value: "walkthrough", label: "Walkthrough" },
    { value: "meeting", label: "Prospect Meeting" },
  ];

  if (!event) return null;

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-[500px] overflow-y-auto sm:max-w-[500px]">
        <SheetHeader>
          <SheetTitle>Edit Meeting</SheetTitle>
          <SheetDescription>Update the meeting details for this lead.</SheetDescription>
        </SheetHeader>
        <div className="space-y-6 py-6">
          <div className="space-y-2">
            <Label htmlFor="title">Meeting Title</Label>
            <Input
              id="title"
              placeholder="Enter meeting title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              placeholder="Enter meeting description (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="eventType">Meeting Type</Label>
            <Select value={eventType} onValueChange={setEventType}>
              <SelectTrigger>
                <SelectValue placeholder="Select meeting type" />
              </SelectTrigger>
              <SelectContent>
                {leadEventTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="startDate">Start Date</Label>
              <Input
                id="startDate"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="endDate">End Date</Label>
              <Input
                id="endDate"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="allDay"
              checked={isAllDay}
              onCheckedChange={(checked) => setIsAllDay(checked as boolean)}
            />
            <Label htmlFor="allDay">All day event</Label>
          </div>

          {!isAllDay && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startTime">Start Time</Label>
                <Input
                  id="startTime"
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="endTime">End Time</Label>
                <Input
                  id="endTime"
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                />
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="location">Location Name</Label>
            <Input
              id="location"
              placeholder="Enter location name (optional)"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="address">Address</Label>
            <Input
              id="address"
              placeholder="Enter address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>Assigned Staff</Label>
            <div className="max-h-32 space-y-2 overflow-y-auto">
              {members.map((member: any) => (
                <div key={member._id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`staff-${member._id}`}
                    checked={assignedStaff.includes(member._id)}
                    onCheckedChange={() => toggleStaffAssignment(member._id)}
                  />
                  <Label htmlFor={`staff-${member._id}`} className="text-sm">
                    {member.name} ({member.email})
                  </Label>
                </div>
              ))}
            </div>
          </div>

          <Button
            className="w-full"
            onClick={handleSubmit}
            disabled={isSubmitting || !title || !startDate || assignedStaff.length === 0}
          >
            {isSubmitting ? "Updating..." : "Update Meeting"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
